import { AppState, User } from '@auth0/auth0-react'
import {
  OnboardPatientContext,
  OnboardPractitionerContext,
  OnboardStudentContext,
} from '@dfh-corp-sites/onboarding/context'
import { isAfter, isValid, parseISO } from 'date-fns'

import {
  OnboardRequestInput,
  PatientOnboardRequestInput,
  PatientOnboardRequestSubtype,
  PractitionerOnboardRequestInput,
  StudentOnboardRequestInput,
} from '../../graphql/search'
import { CustomError, logAndCaptureException, logAndCaptureMessage } from '../../utils/errorTools'
import { left, LeftResult, right, RightResult } from '../../utils/result'
import { sessionOrFallbackStorage } from '../../utils/storage'
import {
  isStateMachineClaim,
  isUlpAccountType,
  UlpAccountType,
  ulpAccountTypes,
  userCategories,
  UserCategory,
  userClaimIds,
} from '../../utils/userClaims'
import {
  machine as patientMachine,
  OnboardPatientClaimParsed,
  OnboardPatientStateValue,
} from './state/machine-patient'
import {
  machine as practitionerMachine,
  OnboardPractitionerClaimParsed,
  OnboardPractitionerStateValue,
} from './state/machine-practitioner'
import {
  machine as studentMachine,
  OnboardStudentClaimParsed,
  OnboardStudentStateValue,
} from './state/machine-student'

/**
 * Identify whether state from Auth0 claim is valid in state machine.
 * Exclude `invalid` state (used as fallback)
 */

export const isValidPatientState = (
  state: string,
): state is Exclude<OnboardPatientStateValue, 'invalid'> => {
  return !!state && state !== 'invalid' && Object.keys(patientMachine.states).includes(state)
}

export const isValidPractitionerState = (
  state: string,
): state is Exclude<OnboardPractitionerStateValue, 'invalid'> => {
  return !!state && state !== 'invalid' && Object.keys(practitionerMachine.states).includes(state)
}

export const isValidStudentState = (
  state: string,
): state is Exclude<OnboardStudentStateValue, 'invalid'> => {
  return !!state && state !== 'invalid' && Object.keys(studentMachine.states).includes(state)
}

/*
  Functions and types to handle Practitioner Form State
  Ref: https://app.shortcut.com/designsforhealth/story/54970/discuss-implement-we-need-to-add-a-toast-message-banner-to-inform-existing-practitioners-they-need-to-upload-their-forms
*/

export type PractitionerFormActionTypes =
  | { type: 'prac_form_initialized' }
  | { type: 'toast_dismissed' }
  | { type: 'toast_selected' }
  | { type: 'commissions_pref_selected' }
  | { type: 'remind_me_later_selected' }
  | { type: 'onboarding_complete' }
  // A form we can only hand off, not receive: the Canadian documents are DocuSign PowerForms that
  // open in a new tab, and the received flags do not flip until the back office records them days
  // later. Opening one is the only signal we get that the practitioner has acted on the reminder.
  | { type: 'form_opened' }

type PractitionerFormAction = PractitionerFormActionTypes['type']

export interface PractitionerFormsState {
  lastEvent: PractitionerFormAction
  notifyAfter: string
}

/**
 * Whether we should raise the documents reminder again. An absent state means the practitioner has
 * never been reminded, and an unparseable or never-persisted `notifyAfter` errs toward reminding.
 */
export const isFormsReminderOverdue = (
  practitionerFormsState: PractitionerFormsState | undefined,
): boolean => {
  if (!practitionerFormsState) {
    return true
  }
  const { lastEvent, notifyAfter } = practitionerFormsState
  const notifyAfterDate = parseISO(notifyAfter)
  return lastEvent === 'prac_form_initialized' || !isValid(notifyAfterDate)
    ? true
    : isAfter(new Date(), notifyAfterDate)
}

export const isPractitionerFormsState = (formState: unknown): formState is PractitionerFormsState =>
  formState != null &&
  typeof formState === 'object' &&
  typeof (formState as Record<string, unknown>).lastEvent === 'string' &&
  typeof (formState as Record<string, unknown>).notifyAfter === 'string'

export type ActiveOnboarding =
  | {
      id: typeof userClaimIds.onboardPatient
      claim: {
        state: OnboardPatientStateValue
        context: OnboardPatientContext
      }
    }
  | {
      id: typeof userClaimIds.onboardPractitioner
      claim: {
        state: OnboardPractitionerStateValue
        context: OnboardPractitionerContext
      }
    }
  | {
      id: typeof userClaimIds.onboardStudent
      claim: {
        state: OnboardStudentStateValue
        context: OnboardStudentContext
      }
    }

type OnboardingClaim = {
  claimId: (typeof userClaimIds)['onboardPatient' | 'onboardPractitioner' | 'onboardStudent']
  validateState: (state: string) => boolean
}

const onboardingClaims: OnboardingClaim[] = [
  {
    claimId: userClaimIds.onboardPatient,
    validateState: isValidPatientState,
  },
  {
    claimId: userClaimIds.onboardPractitioner,
    validateState: isValidPractitionerState,
  },
  {
    claimId: userClaimIds.onboardStudent,
    validateState: isValidStudentState,
  },
] as const

export const getActiveOnboarding = (user?: User): ActiveOnboarding | null => {
  if (!user) return null

  const found = onboardingClaims.find(({ claimId, validateState }) => {
    const claim = user[claimId]
    if (!isStateMachineClaim(claim) || !claim.state) {
      return false
    }
    const state = claim.state.trim()
    if (!validateState(state)) {
      logAndCaptureMessage(`Invalid ${claimId} state "${state}" in user claim`, {
        level: 'warning',
      })
      return false
    }
    return true
  })
  if (!found) return null
  const customerId = user[userClaimIds.netSuiteId]
  const claim = user[found.claimId]
  return {
    id: found.claimId,
    claim: {
      // if the customerId is present, there's no need to continue onboarding
      state: customerId ? 'complete' : claim.state.trim(),
      context: claim.context ?? {},
    },
  }
}

/**
 * Account type the user selected at registration, when the ID token carries a recognized one.
 * dfh-auth0-config has written it since 2025-08-22 (`825e99d`, story 121510); accounts registered
 * before that carry nothing here.
 */
export const getUlpAccountType = (user?: User): UlpAccountType | undefined => {
  const accountType = user?.[userClaimIds.ulpAccountType] as unknown
  return isUlpAccountType(accountType) ? accountType : undefined
}

/** Whether an account type's onboarding applies to a user, and the state to restore it from. */
export interface OnboardingFlow<Claim> {
  /** Show this account type's onboarding. */
  applies: boolean
  /**
   * Persisted machine state to restore, present only when an active onboarding claim is what
   * matched. `null` starts the machine at `invalid`, from where the provider's `RESTORE` moves it
   * straight to `complete` — the path a provisioned account takes once cleanup has deleted its claim.
   */
  claim: Claim | null
}

export interface OnboardingFlows {
  patient: OnboardingFlow<OnboardPatientClaimParsed>
  practitioner: OnboardingFlow<OnboardPractitionerClaimParsed>
  student: OnboardingFlow<OnboardStudentClaimParsed>
  /**
   * A flow will apply once the access token resolves, so `/onboarding` must not redirect yet.
   * `customerId` is decoded from the access token, which `TokenContextProvider` fetches in an effect
   * — a commit after the ID token's claims are readable — and only the student fallback depends on
   * it. Redirecting in that window is the bug this whole fallback exists to prevent (sc-136128).
   *
   * An access token that never delivers the customer id holds this `true` for good, leaving the page
   * on an empty layout. That is the safe direction: mounting the provider on the ID token's
   * `netsuite_id` instead would submit a blank `RequestOnboard`, since the provider's `submit`
   * short-circuits on the access-token value alone. Bounding the wait needs a loading or error signal
   * `TokenContext` does not carry, and only a persistent token failure gets here.
   */
  pending: boolean
}

/**
 * Which account type's onboarding `/onboarding` should show.
 *
 * An active onboarding claim answers outright. `onboardingCleanup$` deletes that claim as soon as
 * NetSuite provisioning finishes, so a provisioned account needs a second signal to keep its
 * confirmation reachable: patient and practitioner have the category their account resolves to, and
 * students have the account type they chose at registration.
 */
export const resolveOnboardingFlows = (
  user: User | undefined,
  userCategory: UserCategory | undefined,
  customerId: string | undefined,
): OnboardingFlows => {
  const activeClaim = getActiveOnboarding(user)

  const patientClaim = activeClaim?.id === userClaimIds.onboardPatient ? activeClaim.claim : null
  const patientApplies = !!patientClaim || userCategory === userCategories.patient

  const practitionerClaim =
    activeClaim?.id === userClaimIds.onboardPractitioner ? activeClaim.claim : null
  const practitionerApplies = !!practitionerClaim || userCategory === userCategories.practitioner

  // `ulp_account_type` names the account type rather than inferring it, and lives in `app_metadata`,
  // so it outlives the claim that cleanup deletes. The `other_professional` category cannot stand in:
  // `getCategory` in dfh-auth0-config reaches it only after `is_employee`, `is_patient` and
  // `is_practitioner`, which makes it the residual professional bucket — defined by what it is not,
  // so it identifies no single account type.
  //
  // The claim is written once at registration and never updated, so it records the original
  // selection, not the current type. A category that has already named a type therefore outranks it:
  // a student NetSuite later recategorizes keeps `student` here for good and would otherwise match
  // two flows at once. dfh-auth0-config orders the two the same way when it derives a price level.
  const studentClaim = activeClaim?.id === userClaimIds.onboardStudent ? activeClaim.claim : null
  const studentByAccountType =
    !patientApplies &&
    !practitionerApplies &&
    userCategory !== userCategories.employee &&
    getUlpAccountType(user) === ulpAccountTypes.student

  // A customer id keeps the fallback to provisioned accounts. Without one the machine would reach
  // `complete` from an empty context and submit a blank RequestOnboard, since `submit` short-circuits
  // on the customer id alone.
  const studentApplies = !!studentClaim || (!!customerId && studentByAccountType)

  return {
    patient: { applies: patientApplies, claim: patientClaim },
    practitioner: { applies: practitionerApplies, claim: practitionerClaim },
    student: { applies: studentApplies, claim: studentClaim },
    // Provisioned according to the ID token, which carries `netsuite_id` from the same
    // `app_metadata.netsuite_id` the access token's `ns_customer_id` comes from — so the access
    // token is going to confirm it, and the only question is whether it has arrived.
    pending: studentByAccountType && !customerId && !!user?.[userClaimIds.netSuiteId],
  }
}

export const BEFORE_ONBOARDING_STATE = 'beforeOnboardingState'

export const saveBeforeOnboardingState = (state: AppState) => {
  try {
    sessionOrFallbackStorage.setItem(BEFORE_ONBOARDING_STATE, JSON.stringify(state))
  } catch (_) {
    // Ignore storage errors; not critical
  }
}

export const buildOnboardRequestInput = (
  activeOnboarding: ActiveOnboarding,
): OnboardRequestInput | null => {
  if (activeOnboarding?.id === userClaimIds.onboardPatient) {
    const input = activeOnboarding?.claim.context
    return {
      patient: {
        // TODO verify
        ...(input as Omit<PatientOnboardRequestInput, 'subtype'>),
        subtype:
          input.subtype === 'patient'
            ? PatientOnboardRequestSubtype.Patient
            : PatientOnboardRequestSubtype.Visitor,
      },
    }
  }
  if (activeOnboarding?.id === userClaimIds.onboardPractitioner) {
    const { smsNumber, ...context } = activeOnboarding.claim.context
    return {
      // TODO verify
      practitioner: {
        ...context,
        ...(context.smsOptIn && smsNumber?.trim() ? { smsNumber } : {}),
      } as PractitionerOnboardRequestInput,
    }
  }

  if (activeOnboarding?.id === userClaimIds.onboardStudent) {
    const input = activeOnboarding?.claim.context
    return {
      // TODO verify
      student: input as StudentOnboardRequestInput,
    }
  }

  return null
}

class OnboardRequestParseError extends CustomError {
  override name = 'OnboardRequestParseError'
}

export const isEmailVerificationSentError = (response?: string | null): boolean => {
  if (!response) return false
  try {
    const parsed: unknown = JSON.parse(response)
    if (
      typeof parsed === 'object' &&
      parsed != null &&
      'statusDetails' in parsed &&
      Array.isArray((parsed as { statusDetails: unknown }).statusDetails)
    ) {
      return (parsed as { statusDetails: ReadonlyArray<unknown> }).statusDetails.includes(
        'email_verification_sent',
      )
    } else {
      logAndCaptureException(
        new OnboardRequestParseError(`Parsed response property missing statusDetails: ${response}`),
      )
      return false
    }
  } catch (e) {
    logAndCaptureException(
      new OnboardRequestParseError(
        `Failed to parse response property as JSON`,
        e instanceof Error ? e : new Error(String(e)),
      ),
    )
    return false
  }
}

export function extractActiveOnboardingForOnboardRequest(
  user: User,
): LeftResult<string> | RightResult<ActiveOnboarding> {
  const activeOnboarding = getActiveOnboarding(user)
  if (!activeOnboarding) {
    return left(`Active onboarding claim not found for user ${user.sub}`)
  }
  if (activeOnboarding.claim.state !== 'complete') {
    return left(
      `Active onboarding machine state is not 'complete' (actual: ${activeOnboarding.claim.state})`,
    )
  }
  return right(activeOnboarding)
}
